/**
 * PostHog Cloud US (`https://us.i.posthog.com`) client wrapper & funnel event dispatcher (`MAR-1236` / `MAR-1347` / `MAR-1348`).
 *
 * Invariants:
 * - Zero initialization, network calls, or browser storage when `runtimeConfig.public.posthogKey` is
 *   empty or when effective consent is `false` (strict GDPR/LGPD opt-in before consent, or after opt-out).
 * - Session replay enforces `maskAllInputs: true`, `maskTextSelector: '[data-ph-mask]'`, and
 *   `blockSelector: '[data-ph-block], .ph-no-capture'`.
 * - `before_send` strips `?prompt=` from `$current_url` / `$pathname` so confidential legal queries
 *   never leak into pageview or autocapture metadata.
 * - `identify` sends only `{ organization_id, organization_main_jurisdiction, tier }` keyed by
 *   `firebase_uid`, never `email` or display name.
 */
import posthog from 'posthog-js'
import {
  POSTHOG_PERSISTENCE_MODE,
  POSTHOG_SESSION_RECORDING_CONFIG,
  sanitizeAnalyticsUrl,
  shouldInitializePostHog,
  type AnalyticsRegime,
  type StoredAnalyticsConsent,
} from '~~/shared/analytics/consent'

let posthogInitialized = false
let lastIdentifiedUid: string | null = null

function isBrowserRuntime(): boolean {
  return Boolean(import.meta.client || typeof window !== 'undefined')
}

export interface PostHogIdentityInput {
  uid: string
  organizationName?: string | null
  organizationMainJurisdiction?: string | null
  orgOverrideId?: string | null
}

function sanitizeEventProperties(props: Record<string, unknown> | undefined): void {
  if (!props) return
  if (typeof props.$current_url === 'string') {
    props.$current_url = sanitizeAnalyticsUrl(props.$current_url)
  }
  if (typeof props.$pathname === 'string') {
    props.$pathname = sanitizeAnalyticsUrl(props.$pathname)
  }
}

export function isPostHogInitialized(): boolean {
  return posthogInitialized
}

export function initPostHogClient(options: {
  posthogKey: string
  posthogHost?: string
  regime: AnalyticsRegime
  userConsent?: boolean | null
  storedConsent?: StoredAnalyticsConsent
}): boolean {
  if (!isBrowserRuntime()) return false

  const allowed = shouldInitializePostHog({
    posthogKey: options.posthogKey,
    regime: options.regime,
    userConsent: options.userConsent,
    storedConsent: options.storedConsent,
  })
  if (!allowed) return false

  if (!posthogInitialized) {
    const apiHost = (options.posthogHost || 'https://us.i.posthog.com').trim()
    posthog.init(options.posthogKey.trim(), {
      api_host: apiHost,
      ui_host: 'https://us.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      persistence: POSTHOG_PERSISTENCE_MODE,
      mask_all_text: false,
      mask_all_element_attributes: false,
      session_recording: {
        maskAllInputs: POSTHOG_SESSION_RECORDING_CONFIG.maskAllInputs,
        maskTextSelector: POSTHOG_SESSION_RECORDING_CONFIG.maskTextSelector,
        blockSelector: POSTHOG_SESSION_RECORDING_CONFIG.blockSelector,
      },
      before_send: (event) => {
        if (!event) return null
        sanitizeEventProperties(event.properties as Record<string, unknown> | undefined)
        return event
      },
    })
    posthogInitialized = true
  }

  posthog.opt_in_capturing()
  posthog.startSessionRecording()
  return true
}

export function stopAndResetPostHogClient(): void {
  if (!isBrowserRuntime()) return
  lastIdentifiedUid = null
  if (!posthogInitialized) return
  try {
    posthog.opt_out_capturing()
    posthog.stopSessionRecording()
    posthog.reset()
  } catch {
    // Best-effort teardown when PostHog is blocked by browser extensions
  }
}

export function resetPostHogUser(): void {
  if (!isBrowserRuntime()) return
  lastIdentifiedUid = null
  if (!posthogInitialized) return
  try {
    posthog.reset()
  } catch {
    // Best-effort reset on sign-out
  }
}

export function identifyPostHogUser(identity: PostHogIdentityInput | null): void {
  if (!isBrowserRuntime() || !posthogInitialized || !identity?.uid) return
  const tier = identity.organizationName ? 'workspace' : 'free_chat'
  const signature = `${identity.uid}:${tier}:${identity.organizationMainJurisdiction ?? ''}`
  if (lastIdentifiedUid === signature) return
  lastIdentifiedUid = signature
  try {
    posthog.identify(identity.uid, {
      tier,
      has_organization: Boolean(identity.organizationName),
      organization_main_jurisdiction: identity.organizationMainJurisdiction ?? null,
    })
  } catch {
    // Ignore PostHog network/blocker errors
  }
}

function readPublicPostHogConfig(): { posthogKey: string; posthogHost: string } {
  try {
    if (typeof useRuntimeConfig === 'function') {
      const config = useRuntimeConfig()
      return {
        posthogKey: String(config?.public?.posthogKey || '').trim(),
        posthogHost: String(config?.public?.posthogHost || 'https://us.i.posthog.com').trim(),
      }
    }
  } catch {
    // Fallback when mounted in unit tests outside a full Nuxt instance
  }
  return {
    posthogKey: '',
    posthogHost: 'https://us.i.posthog.com',
  }
}

export function usePostHog() {
  const { posthogKey, posthogHost } = readPublicPostHogConfig()

  function syncPostHogWithConsent(params: {
    regime: AnalyticsRegime
    userConsent?: boolean | null
    storedConsent?: StoredAnalyticsConsent
    identity?: PostHogIdentityInput | null
  }): boolean {
    if (!isBrowserRuntime()) return false
    const allowed = shouldInitializePostHog({
      posthogKey,
      regime: params.regime,
      userConsent: params.userConsent,
      storedConsent: params.storedConsent,
    })

    if (!allowed) {
      stopAndResetPostHogClient()
      return false
    }

    const started = initPostHogClient({
      posthogKey,
      posthogHost,
      regime: params.regime,
      userConsent: params.userConsent,
      storedConsent: params.storedConsent,
    })
    if (started && params.identity?.uid) {
      identifyPostHogUser(params.identity)
    }
    return started
  }

  function captureEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (!isBrowserRuntime() || !posthogInitialized) return
    try {
      const sanitized = properties ? { ...properties } : {}
      sanitizeEventProperties(sanitized)
      posthog.capture(eventName, sanitized)
    } catch {
      // Never break user flows if analytics fails
    }
  }

  function capturePageview(fullPath?: string): void {
    if (!isBrowserRuntime() || !posthogInitialized) return
    try {
      const rawUrl = fullPath
        ? new URL(fullPath, window.location.origin).toString()
        : window.location.href
      const cleanUrl = sanitizeAnalyticsUrl(rawUrl)
      posthog.capture('$pageview', {
        $current_url: cleanUrl,
      })
    } catch {
      // Ignore analytics errors
    }
  }

  return {
    isInitialized: isPostHogInitialized,
    syncPostHogWithConsent,
    captureEvent,
    capturePageview,
    identifyUser: identifyPostHogUser,
    resetUser: resetPostHogUser,
  }
}
